<template>
  <div>
    <!-- Identifier -->
    <v-sheet color="white" elevation="1" class="pa-3">
      <v-row no-gutters class="align-center">
        <v-col cols="2" sm="1">
          <!-- display: block -->
          <!-- The badge is not shown for alarms and exception events as the color of the icon will be changed instead -->
          <v-badge
            bordered
            overlap
            :color="hasAlert(individual) ? 'error' : 'warning'"
            icon="mdi-exclamation-thick"
            :value="
              !isAlarmOrException(individual) &&
              (hasAlert(individual) || hasWarning(individual))
            "
          >
            <v-btn
              fab
              small
              @click="onClick"
              :color="
                isAlarmOrException(individual) && hasAlert(individual)
                  ? 'error'
                  : 'secondary'
              "
            >
              <v-icon>{{ recTypeIcon }}</v-icon>
            </v-btn>
          </v-badge>
        </v-col>
        <v-col cols="10" sm="11">
          <div class="text-caption text-truncate">
            <iri-value :iri="individual.type"></iri-value>
          </div>
          <div class="text-body-1 text-truncate">
            <iri-value :iri="individual.id"></iri-value>
          </div>
        </v-col>
      </v-row>
    </v-sheet>

    <!-- Properties -->
    <v-sheet color="white" elevation="1" class="pa-3 mt-4">
      <v-row>
        <v-col cols="2" sm="1">
          <!-- display: block -->
          <v-btn icon @click.stop="" color="secondary">
            <v-icon>mdi-format-list-bulleted</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="10" sm="11">
          <!-- display: block -->
          <data-properties
            :dataProperties="dataProperties"
          ></data-properties> </v-col
      ></v-row>
    </v-sheet>

    <!-- Components -->
    <v-sheet
      color="white"
      elevation="1"
      class="pa-3 mt-4"
      v-for="component of individual.components"
      :key="component.id.value"
    >
      <v-row>
        <v-col cols="2" sm="1">
          <!-- display: block -->
          <v-badge
            bordered
            overlap
            :color="hasAlert(component) ? 'error' : 'warning'"
            icon="mdi-exclamation-thick"
            :value="
              !isAlarmOrException(component) &&
              (hasAlert(component) || hasWarning(component))
            "
          >
            <v-btn
              icon
              @click.stop=""
              :color="
                isAlarmOrException(component) && hasAlert(component)
                  ? 'error'
                  : 'secondary'
              "
            >
              <v-icon>{{ componentTypeIcon(component.type) }}</v-icon>
            </v-btn>
          </v-badge>
        </v-col>
        <v-col cols="10" sm="11">
          <div class="text-caption text-truncate">
            <!-- display: block -->
            <iri-value :iri="component.type" humanize></iri-value>
            <v-icon small right color="secondary">mdi-vector-link</v-icon>
          </div>
          <div class="text-body-1 text-truncate">
            <iri-value :iri="component.id"></iri-value>
          </div>
          <v-divider class="my-3"></v-divider>
          <!-- display: block -->
          <data-properties
            :dataProperties="component.properties"
          ></data-properties>
        </v-col>
      </v-row>
    </v-sheet>

    <!-- Outgoing -->
    <v-sheet color="white" elevation="1" class="pa-3 mt-4">
      <v-row>
        <v-col cols="2" sm="1">
          <v-btn icon @click.stop="" color="secondary"
            ><v-icon>mdi-export</v-icon></v-btn
          >
        </v-col>
        <v-col cols="10" sm="11">
          <!-- display: block -->
          <object-prop
            v-for="property of objectProperties"
            :key="property.name.value"
            :objectProperty="property"
          ></object-prop></v-col
      ></v-row>
    </v-sheet>

    <!-- Incoming -->
    <v-sheet color="white" elevation="1" class="pa-3 mt-4">
      <v-row>
        <v-col cols="2" sm="1">
          <v-btn icon @click.stop="" color="secondary"
            ><v-icon>mdi-import</v-icon></v-btn
          >
        </v-col>
        <v-col cols="10" sm="11">
          <object-prop
            v-for="property of inverseObjectProperties"
            :key="property.name.value"
            :objectProperty="property"
          ></object-prop>
        </v-col>
      </v-row>
    </v-sheet>
  </div>
</template>

<script>
import IriValue from '@/components/IriValue.vue'
// import DataProp from '@/components/DataProperty.vue'
import DataProperties from '@/components/DataProperties.vue'
import ObjectProp from '@/components/ObjectProperty.vue'
// TODO add guard for reading individual
import { DataProperty, ObjectProperty } from '@/rec-cache/rec-cache-types'
import { recIcon } from '@/vue-wob-cache/vue-wob-icons'
import { RecIndividual } from '@/rec-cache/rec-cache-individual'
import { WobAggregators } from '@/vue-wob-cache/vue-wob-aggregators'
import { Alarms, ExceptionEvent } from '@/vue-wob-cache/vue-wob-alarms'

export default {
  components: { IriValue, /* DataProp, */ DataProperties, ObjectProp },
  props: {
    individual: {
      type: Object,
      default: () => new RecIndividual(),
    },
  },
  mounted: function () {
    console.log('components.individual-expanded.mounted', this.individual)
  },
  // data: () => ({
  //   individual: new RecIndividual(),
  // }),
  computed: {
    dataProperties: function () {
      return this.individual.properties.filter((p) => p instanceof DataProperty)
    },
    objectProperties: function () {
      return this.individual.properties
        .filter((p) => p instanceof ObjectProperty)
        .filter((p) => !WobAggregators.includes(p.name.value))
    },
    inverseObjectProperties: function () {
      const edges = this.$wob.queryEdges({ target: this.individual.id.value })
      const properties = []
      const labels = []
      edges.forEach((e) => {
        if (!labels.includes(e.label.value)) {
          labels.push(e.label.value)
          // TODO using the empty array in constructor seems wrong, should likely be no argument...
          properties.push(new ObjectProperty(e.label, []))
        }
        properties.find((p) => p.name.equals(e.label.value))?.addValue(e.source)
      })
      return properties.filter((p) => !WobAggregators.includes(p.name.value))
    },
    recTypeIcon: function () {
      return recIcon(this.individual.type, this.hasAlert(this.individual))
    },
    // TODO remove when new implementation has been tested...
    // hasException: function () {
    //   return this.$wob.exceptions.ids.includes(this.individual.id.value)
    // },

    // hasComponent: function () {
    //   return !!this.$wob.hasComponents(this.individual.id)
    // },
  },
  methods: {
    componentTypeIcon: function (type) {
      return recIcon(type)
    },
    hasAlert: function (individual) {
      const id = individual.id.value
      return (
        this.$wob.openExceptions.ids.map((id) => id.value).includes(id) ||
        this.$wob.activeAlarms.ids.map((id) => id.value).includes(id) ||
        this.$wob.alarmingAssets.ids.map((id) => id.value).includes(id)
      )
    },
    hasWarning: function (individual) {
      return this.$wob.impactedAssets.ids
        .map((id) => id.value)
        .includes(individual.id.value)
    },

    isAlarmOrException: function (individual) {
      return (
        Alarms.includes(individual.type?.value) ||
        ExceptionEvent == individual.type?.value
      )
    },
    // TODO substitued by other methods, remove after testing...
    // componentException: function (individual) {
    //   return (
    //     this.$wob.exceptions.ids.includes(individual.id.value) ||
    //     this.$wob.impacts.ids.includes(individual.id.value)
    //   )
    // },
    onClick: function (event) {
      console.log('components.individual-expanded.methods.on-click', event)
      const apartmentViewTriggers = [
        'https://w3id.org/rec#Apartment',
        'https://webofbuildings.com/ontology/LmApartment',
        'https://webofbuildings.com/ontology/ResidentialApartment',
      ]
      if (apartmentViewTriggers.includes(this.individual.type.value)) {
        // Route change to apartment view.
        event.stopPropagation()
        this.$router.push(
          `/apartment/${encodeURIComponent(this.individual.id.value)}`
        )
      }
    },
  },
  // beforeRouteUpdate: function (to, from, next) {
  //   console.log('components.individual-view.before-route-update', to)
  //   this.individual = this.$wob.read(new Iri(to.params.id))
  //   next()
  // },

  // mounted: function () {
  //   console.log('views.individual-view.mounted', this.id)
  //   this.individual = this.$wob.read(new Iri(this.id))
  //   console.log('views.individual-view.mounted.individual', this.individual)
  // },
}
</script>
